import { ChangeDetectorRef } from '@angular/core';
import { MonoTypeOperatorFunction, tap } from 'rxjs';

/**
 * Deferral helpers for updates that would otherwise trigger
 * ExpressionChangedAfterItHasBeenCheckedError (e.g. mutating template-bound
 * state inside subscription callbacks during the same change-detection turn).
 *
 * - {@link safeUpdateInSubscription} — `queueMicrotask`; use inside Observable
 *   subscriptions, Promise continuations, or other work already scheduled as
 *   a microtask. Prefer this for routine post-subscribe state updates.
 * - {@link safePropertyUpdate} — `setTimeout` (macrotask); use when a child
 *   component or ViewChild needs an extra render cycle to settle (DOM layout,
 *   mat-select panel, undefined `@Input` bindings briefly during parent CD).
 *   Optional `delay` for chained UI updates.
 * - {@link markForCheckOnEmit} — RxJS operator; marks the view dirty on each
 *   emission/error so plain-field writes in the subscriber still render with
 *   nothing else to trigger change detection.
 */

/**
 * Defers property changes to the next macrotask so child components can finish
 * the current render cycle before inputs are updated.
 */
export function safePropertyUpdate(
	cdr: ChangeDetectorRef,
	updateFn: () => void,
	delay: number = 0
): void {
	setTimeout(() => {
		updateFn();
		cdr.markForCheck();
	}, delay);
}

/**
 * Defers property changes to the next microtask. Put all state mutations that
 * must be deferred inside `updateFn` — do not call with an empty callback.
 *
 * `markForCheck()` is sufficient for OnPush components. Default-strategy
 * components that need an immediate DOM sync may call `cdr.detectChanges()`
 * at the end of `updateFn` once bindings are stable.
 */
export function safeUpdateInSubscription(
	cdr: ChangeDetectorRef,
	updateFn: () => void
): void {
	queueMicrotask(() => {
		updateFn();
		cdr.markForCheck();
	});
}

/**
 * Marks the view for check on every emission and on error, so the subscriber's
 * plain-field writes render without zone.js (zoneless, JEP-10512).
 *
 * Safe to place before the subscriber runs: `markForCheck()` only flags the
 * view and schedules change detection for later, it never renders
 * synchronously. Put it last in the pipe, next to `takeUntil`.
 */
export function markForCheckOnEmit<T>(
	cdr: ChangeDetectorRef
): MonoTypeOperatorFunction<T> {
	return tap<T>({
		next: () => cdr.markForCheck(),
		error: () => cdr.markForCheck(),
	});
}
